/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Exchange, fetchExchange, mapExchange, Operation, Provider } from 'urql';
import { fromPromise, fromValue, map, mergeMap, pipe } from 'wonka';
import { useAuth0 } from '@auth0/auth0-react';
import { createClient } from '@urql/core';
import { cacheExchange as coreCache } from '@urql/core';
import { cacheExchange } from '@urql/exchange-graphcache';

type Props = { children?: React.ReactNode | undefined };

export const Auth0UrqlProvider = ({ children }: Props) => {
  const { logout, getIdTokenClaims } = useAuth0();

  const fetchOptionsExchange =
    (fn: any): Exchange =>
    ({ forward }) =>
    (ops$) => {
      return pipe(
        ops$,
        mergeMap((operation: Operation) => {
          const result = fn(operation.context.fetchOptions);
          return pipe(
            (typeof result.then === 'function' ? fromPromise(result) : fromValue(result)) as any,
            map((fetchOptions: RequestInit | (() => RequestInit)) => ({
              ...operation,
              context: { ...operation.context, fetchOptions },
            })),
          );
        }),
        forward,
      );
    };

  const client = createClient({
    url: import.meta.env.VITE_GRAPHQL_ENDOPOINT || 'https://stg.api.giho.jp/graphql',
    requestPolicy: 'cache-and-network',
    suspense: true,
    exchanges: [
      mapExchange({
        onError(error, _operation) {
          if (error.message.includes('role is not matched')) {
            logout();
          }
        },
      }),
      cacheExchange({
        updates: {
          Mutation: {
            deleteWillboxEstimateFile: (_result, _args, cache, _info) => {
              const key = 'Query';
              cache.inspectFields(key).forEach((field) => {
                console.info(key);
                cache.invalidate(key, field.fieldKey);
                // or alternatively:
                cache.invalidate(key, field.fieldName, field.arguments);
              });
            },
          },
        },
        keys: {
          AwsS3File: (data: any) => data.awsS3Id,
        },
      }),
      fetchOptionsExchange(async (fetchOptions: any) => {
        const token = await getIdTokenClaims();

        return Promise.resolve({
          ...fetchOptions,
          headers: {
            Authorization: token ? `Bearer ${token.__raw}` : '',
          },
        });
      }),
      coreCache,
      fetchExchange,
    ],
  });

  return <Provider value={client}>{children}</Provider>;
};
